/**
 * Dropdown option registry + shared field components for the Organisation tabs.
 *
 * Design goals:
 *  1. **Zero data loss** — if an org has a stored value that isn't in the
 *     proposed dropdown list, the `SelectField` component auto-appends it as
 *     an extra option at the top labelled `"<value> (existing)"`. The user
 *     can keep it or choose a new option.
 *  2. **Strict new values for new selections** — the dropdown itself only
 *     shows the canonical list per the user's spec.
 *  3. **Multi-select checkboxes** (`MultiCheckboxField`) for comma-separated
 *     values (e.g. Customer History 2011..2026) — pre-ticks any tokens that
 *     already exist in the stored value.
 */
import React, { memo, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useJobAssignees } from '../../context/JobAssigneesContext';
import { JobAssigneesManagerDialog } from '../JobAssigneesManagerDialog';
import { Settings2 } from 'lucide-react';

// ============================================================================
// OPTION REGISTRY (copied verbatim from user spec)
// ============================================================================
export const FIELD_OPTIONS = {
  marketing_status: [
    'Booked with GS 2026', 'Booked with GS 2027', 'DO NOT MARKET',
    'Potential', 'Not Interested in 2026', 'Not interested in 2027',
  ],
  new_or_existing: ['New', 'Existing'],
  package_selected: [
    '$5 ID Card', '2026 Dance', '2026 High School Package',
    '2026 Pre School Package', '2026 School Package',
    '2026 Summer Sport Package', '2026 Winter Sport Package',
    'Graduation', 'ID Cards', 'Year 12 Graduation', 'Year 6 Graduation',
  ],
  envelope: [
    'CUSTOM', 'Flyer Only', 'High School', 'Preschool Envelope',
    'Graduation Envelope', 'School - Classic', 'Sports Envelope', 'Super Saver Option',
  ],
  payment_system: ['Cash Only', 'Invoice', 'Online', 'Other', 'Prepaid', 'View'],
  indoor_or_outdoor: [
    'Curtain', 'Dark Grey', 'Grey', 'Indoor', 'Indoor Groupo',
    'New Grey Backdrop', 'Outdoor', 'Outdoor Group',
  ],
  graduation: ['Later in the Year', 'No', 'Photo Day 2', 'TBC', 'Yes'],
  gowns_required: ['No', 'TBC', 'Yes'],
  school_or_club_to_proof: [
    'No', 'Proof Hard Copy', 'Proof Layout',
    'Proof Sponsor Logos', 'TBC', 'Yes',
  ],
  id_card_type: ['ID Card Type', 'Mag Strip', 'MiFare', 'Plain White', 'TBA'],
  id_cards: ['Yes', 'No'],
  muck_up_photos: [
    'For staff only', 'No', 'TBC', 'Year 12', 'Year 6',
    'Year 6 & Staff', 'Yes', 'Yes Staff & Children',
  ],
  staff_photos: ['Composite', 'Plus Staff Muck Up', 'TBC', 'Traditional'],
  around_school_photos: ['Ladder Required', 'No', 'TBC', 'Yes'],
  whole_school_photo: [
    'Composite', 'Ladder Required', 'No', 'TBC', 'Traditional',
  ],
  commission_structure: [
    '$1 Rebate on Pack B, C, D, E', '$1 per Student',
    '$2 rebate per pack', '$3 rebate per pack', '$5 rebate per pack',
    '$9 rebate per pack', '10% commission', '13% commission',
    '15% commission', '17% commission', '20% commission',
    'As per 2026 Brouchure', 'As per 2027 Brouchure', 'No commission',
  ],
  schedule_received: ['Draft Only - To be Confirmed', 'No', 'Yes'],
  names_received: ['No', 'Not Required', 'Yes'],
  logo_on_file: [
    '2 Logos Required', 'More than 2 Logos Required',
    'Celebrating 50 Years Logo', 'No', 'Yes',
  ],
  inserts: ['Yes', 'No'],
  sponsors: ['Yes', 'No'],
  names_typed: ['Yes', 'No'],
  job_assigned_to: [
    'Bri', 'Britt', 'Cece', 'Kim', 'Leah', 'Nicole', 'Parveen', 'William',
  ],
  dispatch_type: ['Courier', 'Hand Delivered'],
  commission_required: [
    '$9 Rebate', '13% Commission', 'Plus Last Years Commission',
    'Using Commission for ID Cards', 'Yes', 'No',
  ],
};

// Customer History is a MULTI-select (comma-separated) — 2026..2011 (descending)
export const CUSTOMER_HISTORY_OPTIONS = Array.from(
  { length: 2026 - 2011 + 1 },
  (_, i) => String(2026 - i)
);

// Sentinel used by SelectField to clear the stored value via the dropdown
const CLEAR_SENTINEL = '__CLEAR__';

// ============================================================================
// SelectField — dropdown with smart preservation of legacy values
// ============================================================================
export const SelectField = memo(({ label, field, defaultValue, onFieldChange }) => {
  // Coerce to string defensively — incoming value could be null, number, bool, etc.
  const toSafeString = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v.trim();
    return String(v).trim();
  };
  const [value, setValue] = useState(toSafeString(defaultValue));
  // Job Assigned To is user-managed at runtime (via
  // /api/job-assignees). Every OTHER field still reads from the
  // hard-coded FIELD_OPTIONS registry above.
  const jobAssignees = useJobAssignees();
  const [manageOpen, setManageOpen] = useState(false);
  const proposed = field === 'job_assigned_to'
    ? jobAssignees.names
    : (FIELD_OPTIONS[field] || []);
  const currentNormalized = toSafeString(value);
  // A value is "legacy" only if:
  //   1. It's non-empty
  //   2. It's NOT already in the proposed list (avoids duplicate SelectItems)
  //   3. It's NOT the reserved clear sentinel (avoids colliding with the
  //      `— clear —` item and breaking Radix invariants)
  const isLegacy =
    !!currentNormalized &&
    currentNormalized !== CLEAR_SENTINEL &&
    !proposed.includes(currentNormalized);

  return (
    <div className="flex items-center justify-between py-1.5 border-b border-muted/50 last:border-0">
      <span className="text-sm text-muted-foreground w-44 shrink-0">{label}:</span>
      <div className="flex items-center gap-1 w-full">
        <Select
          value={currentNormalized || undefined}
          onValueChange={(v) => {
            const next = v === CLEAR_SENTINEL ? '' : v;
            setValue(next);
            onFieldChange(field, next);
          }}
        >
          <SelectTrigger
            className="h-7 w-full text-sm"
            data-testid={`select-${field}`}
          >
            <SelectValue placeholder="-" />
          </SelectTrigger>
          <SelectContent>
            {isLegacy && (
              <SelectItem value={currentNormalized} className="text-amber-600">
                {currentNormalized} (existing)
              </SelectItem>
            )}
            <SelectItem value={CLEAR_SENTINEL} className="text-muted-foreground italic">
              — clear —
            </SelectItem>
            {proposed
              .filter((opt) => typeof opt === 'string' && opt.length > 0)
              .map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        {field === 'job_assigned_to' && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setManageOpen(true); }}
            className="h-7 px-1.5 rounded border border-input bg-background text-muted-foreground hover:bg-muted hover:text-foreground shrink-0"
            title="Add, rename, or remove names"
            data-testid="job-assignees-manage-btn"
          >
            <Settings2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {field === 'job_assigned_to' && (
        <JobAssigneesManagerDialog open={manageOpen} onOpenChange={setManageOpen} />
      )}
    </div>
  );
});
SelectField.displayName = 'SelectField';

// ============================================================================
// MultiCheckboxField — multi-select checkboxes, stores as comma-separated
// Used for Customer History (2011..2026)
// ============================================================================
export const MultiCheckboxField = memo(({ label, field, defaultValue, onFieldChange, options }) => {
  // Initial selection — tokenise existing comma-separated value
  const parseTokens = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v.map(String).map((x) => x.trim()).filter(Boolean);
    return String(v).split(',').map((t) => t.trim()).filter(Boolean);
  };
  const [selected, setSelected] = useState(new Set(parseTokens(defaultValue)));

  const toggle = (opt) => {
    const next = new Set(selected);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    setSelected(next);
    // Serialize back in option order so storage is stable
    const ordered = options.filter((o) => next.has(o));
    // Append any legacy (existing) tokens the user kept
    parseTokens(defaultValue).forEach((t) => {
      if (!options.includes(t) && next.has(t)) ordered.push(t);
    });
    onFieldChange(field, ordered.join(', '));
  };

  // Include any legacy tokens at the top so they're preserved
  const storedTokens = parseTokens(defaultValue);
  const legacyTokens = storedTokens.filter((t) => !options.includes(t));

  return (
    <div className="flex items-start justify-between py-1.5 border-b border-muted/50 last:border-0">
      <span className="text-sm text-muted-foreground w-44 shrink-0 pt-1">{label}:</span>
      <div className="flex w-full flex-wrap gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm">
        {legacyTokens.map((t) => (
          <label
            key={`legacy-${t}`}
            className="flex items-center gap-1 cursor-pointer text-amber-600"
            title="Existing value (kept)"
          >
            <input
              type="checkbox"
              checked={selected.has(t)}
              onChange={() => toggle(t)}
              className="h-3 w-3 cursor-pointer"
              data-testid={`multi-${field}-${t}`}
            />
            <span>{t}</span>
          </label>
        ))}
        {options.map((opt) => (
          <label
            key={opt}
            className="flex items-center gap-1 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selected.has(opt)}
              onChange={() => toggle(opt)}
              className="h-3 w-3 cursor-pointer"
              data-testid={`multi-${field}-${opt}`}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
});
MultiCheckboxField.displayName = 'MultiCheckboxField';
